import * as THREE from 'three';
import { CubeFace } from '../Types';
import { SpatialIndex } from './SpatialIndex';
import { COVERAGE } from './constants';

/**
 * Tile replacement decision
 */
export interface ReplacementDecision {
  tilesToHide: string[];
  tilesToRemove: string[];
  shouldPreserveBaseTiles: boolean;
  reason: string;
}

/**
 * Coverage analysis result
 */
export interface CoverageAnalysis {
  totalTiles: number;
  tilesByLevel: Record<number, number>;
  tilesByFace: Record<string, number>;
  faceCoverage: number;
  hasMinimumCoverage: boolean;
  hasExcellentCoverage: boolean;
}

/**
 * Replacement policy configuration
 */
export interface ReplacementPolicyConfig {
  minSafeTileCount: number;
  minRequiredTiles: number;
  minFaceCoverage: number;
  maxRemovalRatio: number;
  maxTransitionRemovalRatio: number;
  excellentCoverageMultiplier: number;
}

/**
 * Manages tile replacement decisions and safety policies
 * - Enforces single visible LOD per spatial area to prevent z-fighting flicker.
 * - Uses SpatialIndex’s new structure (area → level → tileKey → mesh).
 * - Computes coverage safely (faces with >0 tiles).
 */
export class ReplacementPolicy {
  private readonly spatialIndex: SpatialIndex;
  private readonly config: ReplacementPolicyConfig;

  constructor(
    spatialIndex: SpatialIndex,
    config: Partial<ReplacementPolicyConfig> = {}
  ) {
    this.spatialIndex = spatialIndex;
    this.config = {
      minSafeTileCount: COVERAGE.MIN_SAFE_TILE_COUNT,
      minRequiredTiles: COVERAGE.MIN_REQUIRED_TILES,
      minFaceCoverage: COVERAGE.MIN_FACE_COVERAGE,
      maxRemovalRatio: COVERAGE.MAX_REMOVAL_RATIO,
      maxTransitionRemovalRatio: COVERAGE.MAX_TRANSITION_REMOVAL_RATIO,
      excellentCoverageMultiplier: COVERAGE.EXCELLENT_COVERAGE_MULTIPLIER,
      ...config,
    };
  }

  /**
   * Analyze current tile coverage
   * - faceCoverage: number of faces that currently have ≥1 tile (NOT just number of keys)
   */
  analyzeCoverage(): CoverageAnalysis {
    const stats = this.spatialIndex.getStats();

    // Count only faces with > 0 tiles
    const facesWithTiles = Object.values(stats.tilesByFace).filter((n) => (n ?? 0) > 0).length;

    const hasMinimumCoverage =
      stats.totalTiles >= this.config.minRequiredTiles &&
      facesWithTiles >= this.config.minFaceCoverage;

    const hasExcellentCoverage =
      stats.totalTiles >= this.config.minSafeTileCount &&
      facesWithTiles >= 6; // All faces covered

    return {
      totalTiles: stats.totalTiles,
      tilesByLevel: stats.tilesByLevel,
      tilesByFace: stats.tilesByFace,
      faceCoverage: facesWithTiles,
      hasMinimumCoverage,
      hasExcellentCoverage,
    };
  }

  /**
   * Decide which tiles to replace when a new higher-level tile is loaded
   * - Enforces single visible LOD in `spatialKey` to avoid z-fighting.
   */
  decideReplacement(
    spatialKey: string,
    newLevel: number,
    targetLODLevel: number,
    isRapidTransition: boolean
  ): ReplacementDecision {
    const coverage = this.analyzeCoverage();

    // Safety check: not enough tiles loaded yet
    if (!coverage.hasMinimumCoverage) {
      return {
        tilesToHide: [],
        tilesToRemove: [],
        shouldPreserveBaseTiles: true,
        reason: 'Insufficient tile coverage for safe replacement',
      };
    }

    const levels = this.spatialIndex.getSpatialTiles(spatialKey);
    if (!levels || levels.size === 0) {
      return {
        tilesToHide: [],
        tilesToRemove: [],
        shouldPreserveBaseTiles: false,
        reason: 'No tiles found in spatial area',
      };
    }


    const tilesToHide: string[] = [];
    const tilesToRemove: string[] = [];
    const shouldPreserveBaseTiles = isRapidTransition || newLevel < targetLODLevel;

    // Hide (and optionally remove) lower LODs in this area
    for (const [level, tileMap] of levels) {
      if (level >= newLevel) continue;

      for (const [tileKey, mesh] of tileMap) {
        const ud = mesh.userData || {};
        const lvl = (ud.level as number) ?? level;

        // Preserve L1 if requested (to keep a coarse safety net during fast motion)
        if (shouldPreserveBaseTiles && lvl === 1) continue;

        // Defer visibility changes to TransitionCoordinator (coverage-aware)
        // Just suggest hides; coordinator will decide when it's safe.
        tilesToHide.push(tileKey);

        // Only mark for removal if we have excellent global coverage
        if (coverage.hasExcellentCoverage) {
          tilesToRemove.push(tileKey);
        }
      }
    }

    // Removal limits (global cap)
    const maxRemovalsAllowed = Math.floor(
      coverage.totalTiles *
        (isRapidTransition
          ? this.config.maxTransitionRemovalRatio
          : this.config.maxRemovalRatio)
    );

    if (tilesToRemove.length > maxRemovalsAllowed) {
      // Keep only the safest removals (highest levels first)
      const tilesWithLevels = tilesToRemove.map((tileKey) => {
        const mesh = this.spatialIndex.getTile(tileKey);
        const lvl =
          (mesh?.userData?.level as number | undefined) ??
          // last resort: parse tileKey "FACE_L#_x_y"
          safeParseLevelFromTileKey(tileKey);
        return { tileKey, level: lvl ?? 0 };
      });

      tilesWithLevels.sort((a, b) => b.level - a.level);
      const safeTilesToRemove = tilesWithLevels
        .slice(0, maxRemovalsAllowed)
        .map((t) => t.tileKey);

      return {
        tilesToHide,
        tilesToRemove: safeTilesToRemove,
        shouldPreserveBaseTiles,
        reason: `Limited removal to ${maxRemovalsAllowed} tiles for safety`,
      };
    }

    return {
      tilesToHide,
      tilesToRemove,
      shouldPreserveBaseTiles,
      reason: 'Normal replacement based on coverage analysis',
    };
  }

  /**
   * Decide whether to remove level 1 tiles for a face
   */
  shouldRemoveLevel1ForFace(
    face: CubeFace,
    newLevel: number,
    targetLODLevel: number,
    isRapidTransition: boolean
  ): boolean {
    // Never remove level 1 during rapid transitions
    if (isRapidTransition) return false;

    // Only remove if we have really good coverage
    const coverage = this.analyzeCoverage();
    if (!coverage.hasExcellentCoverage) return false;

    // Only remove if we've reached or exceeded the target LOD level
    if (newLevel < targetLODLevel) return false;

    // Check if this face already has level 1 tiles removed
    if (this.spatialIndex.isLevel1FaceRemoved(face)) return false;

    // Require excellent face coverage at this LOD
    const faceCoverageForCurrentLOD = this.spatialIndex.getFaceCoverageForLevel(newLevel);
    return faceCoverageForCurrentLOD >= this.config.minFaceCoverage;
  }

  /**
   * Get safe batch size for tile removal
   */
  getSafeBatchSize(totalTilesToRemove: number, currentTileCount: number): number {
    // Never remove more than a small percentage at once
    const maxBatchSize = Math.floor(currentTileCount * 0.1); // Max 10% per batch
    const minBatchSize = 1;
    const defaultBatchSize = Math.max(1, Math.floor(totalTilesToRemove / 6));

    return Math.max(minBatchSize, Math.min(maxBatchSize, defaultBatchSize));
  }

  /**
   * Check if it's safe to remove tiles based on current coverage
   */
  isSafeToRemoveTiles(tilesToRemove: string[]): boolean {
    const coverage = this.analyzeCoverage();

    // Check minimum tile count after removal
    const tilesAfterRemoval = coverage.totalTiles - tilesToRemove.length;
    if (tilesAfterRemoval < this.config.minRequiredTiles) {
      return false;
    }

    // Check that we won't leave any face without tiles
    const faceCountAfterRemoval = this.calculateFaceCoverageAfterRemoval(tilesToRemove);
    if (faceCountAfterRemoval < this.config.minFaceCoverage) {
      return false;
    }

    // Check removal ratio
    const removalRatio = tilesToRemove.length / coverage.totalTiles;
    if (removalRatio > this.config.maxRemovalRatio) {
      return false;
    }

    return true;
  }

  /**
   * Calculate face coverage after removing specified tiles
   */
  private calculateFaceCoverageAfterRemoval(tilesToRemove: string[]): number {
    const toRemove = new Set(tilesToRemove);
    const facesWithTiles = new Set<string>();

    // We iterate tracked tiles (all currently tracked), not just keys
    const tracked = this.spatialIndex.getVisibleTiles();
    for (const [tileKey, mesh] of tracked) {
      if (toRemove.has(tileKey)) continue;
      const face = String(mesh.userData?.face ?? String(tileKey).split('_')[0]);
      facesWithTiles.add(face);
    }

    return facesWithTiles.size;
  }

  /**
   * Filter tiles for safe removal based on policy
   */
  filterSafeRemovals(
    candidateTiles: string[],
    isTransitioning: boolean
  ): string[] {
    if (candidateTiles.length === 0) return [];

    const coverage = this.analyzeCoverage();

    // Use more conservative limits during transitions
    const maxRemovalRatio = isTransitioning
      ? this.config.maxTransitionRemovalRatio
      : this.config.maxRemovalRatio;

    const maxRemovals = Math.floor(coverage.totalTiles * maxRemovalRatio);

    // If the full set is within limit and safe, take it as-is
    if (candidateTiles.length <= maxRemovals) {
      if (this.isSafeToRemoveTiles(candidateTiles)) return candidateTiles;
    }

    // Otherwise, sort by "safety" (higher LODs first; avoid base tiles)
    const tilesWithMeta = candidateTiles.map((tileKey) => {
      const mesh = this.spatialIndex.getTile(tileKey);
      const lvl =
        (mesh?.userData?.level as number | undefined) ??
        safeParseLevelFromTileKey(tileKey);
      const face = String(mesh?.userData?.face ?? String(tileKey).split('_')[0]);

      return {
        tileKey,
        level: lvl ?? 0,
        face,
        // Higher levels safer to remove; avoid L1
        safetyScore: (lvl ?? 0) + ((lvl ?? 0) > 1 ? 10 : 0),
      };
    });

    tilesWithMeta.sort((a, b) => b.safetyScore - a.safetyScore);

    const picked = tilesWithMeta
      .slice(0, Math.min(maxRemovals, candidateTiles.length))
      .map((t) => t.tileKey);

    return this.isSafeToRemoveTiles(picked) ? picked : [];
  }

  /**
   * Get replacement policy recommendations
   */
  getRecommendations(): string[] {
    const coverage = this.analyzeCoverage();
    const recs: string[] = [];

    if (!coverage.hasMinimumCoverage) {
      recs.push('Increase tile loading to reach minimum coverage');
    }

    if (coverage.faceCoverage < 6) {
      recs.push('Load tiles for all cube faces');
    }

    if (coverage.totalTiles < this.config.minSafeTileCount) {
      recs.push('Load more tiles before enabling tile removal');
    }

    const levelCounts = Object.values(coverage.tilesByLevel);
    const onlyBase =
      levelCounts.length === 1 && (coverage.tilesByLevel[1] ?? 0) > 0;
    if (onlyBase) {
      recs.push('Load higher LOD tiles for better quality');
    }

    return recs;
  }

  /**
   * Update policy configuration
   */
  updateConfig(newConfig: Partial<ReplacementPolicyConfig>): void {
    Object.assign(this.config, newConfig);
  }

  /**
   * Get current policy configuration
   */
  getConfig(): ReplacementPolicyConfig {
    return { ...this.config };
  }

  /**
   * Validate policy configuration
   */
  validateConfig(): { isValid: boolean; issues: string[] } {
    const issues: string[] = [];
    const cfg = this.config;

    if (cfg.minSafeTileCount <= 0) issues.push('minSafeTileCount must be > 0');
    if (cfg.minRequiredTiles <= 0) issues.push('minRequiredTiles must be > 0');
    if (cfg.minFaceCoverage < 1 || cfg.minFaceCoverage > 6) {
      issues.push('minFaceCoverage must be between 1 and 6');
    }
    if (cfg.maxRemovalRatio <= 0 || cfg.maxRemovalRatio > 1) {
      issues.push('maxRemovalRatio must be between 0 and 1');
    }
    if (cfg.maxTransitionRemovalRatio <= 0 || cfg.maxTransitionRemovalRatio > 1) {
      issues.push('maxTransitionRemovalRatio must be between 0 and 1');
    }
    if (cfg.minRequiredTiles > cfg.minSafeTileCount) {
      issues.push('minRequiredTiles should not exceed minSafeTileCount');
    }

    return { isValid: issues.length === 0, issues };
  }


}


  /** Fallback: parse L# from tileKey like "F_L3_x_y". Returns undefined if not matched. */
  function safeParseLevelFromTileKey(tileKey: string): number | undefined {
    // Expected: FACE_L<level>_x_y (e.g., FRONT_L3_2_1)
    const parts = String(tileKey).split('_');
    if (parts.length < 2) return undefined;
    const l = parts[1];
    if (!l || l[0] !== 'L') return undefined;
    const n = parseInt(l.slice(1), 10);
    return Number.isFinite(n) ? n : undefined;
  }